// ============================================================
// ByOurConsent.com -- Auth Context
// Global user state -- wraps entire app
// Any component can call useAuth() to get current user
// ============================================================

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user,    setUser]    = useState(null);
    const [loading, setLoading] = useState(true);

    // On app load -- check if we have a stored token
    useEffect(() => {
        const token = localStorage.getItem('boc_token');
        if (token) {
            api.get('/auth/me')
                .then(res => setUser(res.data.user))
                .catch(() => {
                    localStorage.removeItem('boc_token');
                    localStorage.removeItem('boc_refresh');
                    localStorage.removeItem('boc_user');
                })
                .finally(() => setLoading(false));
        } else {
            setLoading(false);
        }
    }, []);

    // Register
    const register = useCallback(async (data) => {
        const res = await api.post('/auth/register', data);
        localStorage.setItem('boc_token',   res.data.token);
        localStorage.setItem('boc_refresh', res.data.refreshToken);
        localStorage.setItem('boc_user',    JSON.stringify(res.data.user));
        setUser(res.data.user);
        // Migrate guest cart to user cart
        const guestCart = sessionStorage.getItem('boc_cart_guest');
        if (guestCart) {
            localStorage.setItem(`boc_cart_${res.data.user.id}`, guestCart);
            sessionStorage.removeItem('boc_cart_guest');
            window.dispatchEvent(new CustomEvent('cart:login', { detail: { userId: res.data.user.id } }));
        }
        return res.data;
    }, []);

    // Login
    const login = useCallback(async (email, password) => {
        const res = await api.post('/auth/login', { email, password });
        localStorage.setItem('boc_token',   res.data.token);
        localStorage.setItem('boc_refresh', res.data.refreshToken);
        localStorage.setItem('boc_user',    JSON.stringify(res.data.user));
        // Migrate guest cart to user cart on login
        const guestCart = sessionStorage.getItem('boc_cart_guest');
        if (guestCart) {
            localStorage.setItem(`boc_cart_${res.data.user.id}`, guestCart);
            sessionStorage.removeItem('boc_cart_guest');
            window.dispatchEvent(new CustomEvent('cart:login', { detail: { userId: res.data.user.id } }));
        } else {
            window.dispatchEvent(new CustomEvent('cart:login', { detail: { userId: res.data.user.id } }));
        }
        setUser(res.data.user);
        return res.data;
    }, []);

    // Logout
    const logout = useCallback(async () => {
        try { await api.post('/auth/logout'); } catch {}
        localStorage.removeItem('boc_token');
        localStorage.removeItem('boc_refresh');
        localStorage.removeItem('boc_user');
        // Clear cart on logout
        sessionStorage.removeItem('boc_cart_guest');
        window.dispatchEvent(new Event('cart:clear'));
        setUser(null);
    }, []);

    // Refresh user data from server
    const refreshUser = useCallback(async () => {
        try {
            const res = await api.get('/auth/me');
            setUser(res.data.user);
            localStorage.setItem('boc_user', JSON.stringify(res.data.user));
        } catch {}
    }, []);

    const isAdmin       = user?.role === 'admin' || user?.role === 'superadmin';
    const isGroupLeader = user?.role === 'group_leader' || isAdmin;

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            isAdmin,
            isGroupLeader,
            isLoggedIn: !!user,
            register,
            login,
            logout,
            refreshUser
        }}>
            {children}
        </AuthContext.Provider>
    );
};

// Hook -- use in any component
export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
};

export default AuthContext;
